import { useGtm } from '@gtm-support/vue-gtm'
import type { ObjektDTO } from '~/types/TypesFromPortalBackend'
import type PriceCalculatorResult from '~/types/price-calculator/PriceCalculatorResult'
import PriceCalculatorResultStatusEnum from '~/types/price-calculator/PriceCalculatorResultStatusEnum'

type PriceCalculatorResultSuccess = Extract<PriceCalculatorResult, { status: PriceCalculatorResultStatusEnum.Success }>
type PriceCalculatorResultPreview = Extract<PriceCalculatorResult, { status: PriceCalculatorResultStatusEnum.Preview }>

class ProductsModule {
  sendProductImpressionEvent (
    products: [],
    guests: string,
    arrivalDate: string,
    departureDate: string
  ): void {
    const gtm = useGtm()
    if (!gtm?.enabled()) {
      return
    }
    const infoProducts = []
    products.forEach((product) => {
      infoProducts.push(this.getProduct(product, '', guests, arrivalDate, departureDate))
    })
    gtm.trackEvent({
      event: 'productImpression',
      ecommerce: {
        impressions: infoProducts
      }
    })
  }

  sendViewItemEvent (
    product: ObjektDTO,
    result: PriceCalculatorResultSuccess|PriceCalculatorResultPreview
  ): void {
    const gtm = useGtm()
    if (!gtm?.enabled()) {
      return
    }

    if (product.buchungssystem === 'anfrage') {
      return
    }

    const isSuccess = result.status === PriceCalculatorResultStatusEnum.Success
    const isPreview = result.status === PriceCalculatorResultStatusEnum.Preview

    if (!isSuccess && !isPreview) {
      return
    }

    gtm.trackEvent({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_id: product.nr?.toString() || '',
            item_name: product.hausname || '',
            affiliation: '',
            coupon: '',
            currency: 'EUR',
            discount: 0,
            index: 0,
            item_brand: product.user_nr?.toString() || '',
            item_category: isSuccess ? result.arrival : '',
            item_category2: isSuccess ? result.departure : '',
            item_category3: (isSuccess ? result.personen?.toString() : '') || '',
            item_category4: isSuccess ? result.nights.toString() : '',
            item_category5: product.booking_model || '',
            item_list_id: '',
            item_list_name: '',
            item_variant: product.haustyp || '',
            location_id: product.address?.ort || '',
            // The total_price is used here instead of the provisions_aufschlag.
            // provisions_aufschlag is not fetched for the imported/schnittstellen objects.
            // WHEN provisions_aufschlag can be properly fetched for the schnittstellen,
            // this could change.
            // price: result.provisions_aufschlag || 0,
            price: (isSuccess ? result.total_price : result.min_price_per_night) || 0,
            quantity: 1
          }
        ]
      }
    })
  }

  sendProductClickEvent (
    product: any,
    guests: string,
    arrivalDate: string,
    departureDate: string
  ): void {
    const gtm = useGtm()
    if (!gtm?.enabled()) {
      return
    }
    const infoProduct = this.getProduct(product, '', guests, arrivalDate, departureDate)
    gtm.trackEvent({
      event: 'productClick',
      ecommerce: {
        click: {
          actionField: { list: 'Suchergebnis dynamisch Liste' },
          products: [infoProduct]
        }
      }
    })
  }

  sendAddToCartEvent (
    product: ObjektDTO,
    result: PriceCalculatorResultSuccess
  ): void {
    const gtm = useGtm()
    if (!gtm?.enabled()) {
      return
    }

    if (result.status !== PriceCalculatorResultStatusEnum.Success) {
      return
    }

    gtm.trackEvent({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_id: product.nr?.toString() || '',
            item_name: product.hausname || '',
            affiliation: '',
            coupon: '',
            currency: 'EUR',
            discount: 0,
            index: 0,
            item_brand: product.user_nr?.toString() || '',
            item_category: result.arrival,
            item_category2: result.departure,
            item_category3: result.personen?.toString() || '',
            item_category4: result.nights.toString(),
            item_category5: product.booking_model || '',
            item_list_id: '',
            item_list_name: '',
            item_variant: product.haustyp || '',
            location_id: product.address?.ort || '',
            // The total_price is used here instead of the provisions_aufschlag.
            // provisions_aufschlag is not fetched for the imported/schnittstellen objects.
            // WHEN provisions_aufschlag can be properly fetched for the schnittstellen,
            // this could change.
            // price: result.provisions_aufschlag || 0,
            price: result.total_price || 0,
            quantity: 1
          }
        ]
      }
    })
  }

  private getProduct (
    product: object,
    pricePerNight: string,
    guests: string,
    arrivalDate: string,
    departureDate: string
  ) {
    const hasValidDates = arrivalDate && arrivalDate !== '' && departureDate && departureDate !== ''

    const info = {
      id: product?.nr,
      name: product?.hausname,
      category: product?.haustyp,
      dimension1: product?.user_nr,
      dimension7: product?.address?.ort ?? product?.ortsname,
      dimension8: product?.buchungssystem,
      price: pricePerNight !== '' ? pricePerNight : product.preis_min
    }

    // Early escape: only add date-related dimensions if dates are valid
    if (!hasValidDates) {
      return info
    }

    const startDate = this.parseDate(arrivalDate)
    const endDate = this.parseDate(departureDate)
    const timeDifference = endDate.getTime() - startDate.getTime()
    const flexNights = Math.round(timeDifference / (1000 * 3600 * 24))

    return {
      ...info,
      dimension2: arrivalDate,
      dimension3: departureDate,
      dimension5: guests,
      dimension6: flexNights
    }
  }

  private parseDate (str: string): Date {
    const parts = str.split('.')
    return new Date(
      Number(parts[2]),
      Number(parts[1]) - 1,
      Number(parts[0])
    )
  }
}

export default ProductsModule
